import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FormControl } from 'react-bootstrap'
import { FiChevronDown, FiChevronUp, FiSearch, FiX, FiCheck } from 'react-icons/fi'

function SearchableSelect ({
  options,
  value,
  onChange,
  placeholder = 'Cerca...',
  emptyLabel = 'Nessuna selezione',
  getOptionLabel,
  getOptionValue,
  getOptionSearchText,
  disabled = false
}) {
  const containerRef = useRef(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const selected = useMemo(
    () => options.find(option => getOptionValue(option) === value),
    [options, value, getOptionValue]
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return options
    return options.filter(option => {
      const text = (getOptionSearchText ? getOptionSearchText(option) : getOptionLabel(option)).toLowerCase()
      return text.includes(q)
    })
  }, [options, query, getOptionLabel, getOptionSearchText])

  useEffect(() => {
    function handleClickOutside (event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function selectOption (option) {
    onChange(getOptionValue(option))
    setQuery('')
    setOpen(false)
  }

  function clearSelection () {
    onChange('')
    setQuery('')
    setOpen(false)
  }

  return (
    <div className={`searchable-select${disabled ? ' is-disabled' : ''}`} ref={containerRef}>
      <button
        type='button'
        className='searchable-select-trigger'
        onClick={() => !disabled && setOpen(current => !current)}
        disabled={disabled}
      >
        <span className={selected ? 'searchable-select-value' : 'searchable-select-placeholder'}>
          {selected ? getOptionLabel(selected) : emptyLabel}
        </span>
        {open ? <FiChevronUp className='searchable-select-chevron' /> : <FiChevronDown className='searchable-select-chevron' />}
      </button>

      {open && (
        <div className='searchable-select-panel'>
          <div className='searchable-select-search'>
            <FiSearch className='searchable-select-search-icon' />
            <FormControl
              autoFocus
              value={query}
              placeholder={placeholder}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'Escape') setOpen(false) }}
            />
          </div>

          <button type='button' className='searchable-select-clear' onClick={clearSelection}>
            <FiX /> {emptyLabel}
          </button>

          <div className='searchable-select-list'>
            {filtered.length === 0 && (
              <div className='searchable-select-empty'>Nessun risultato</div>
            )}
            {filtered.map(option => {
              const optionValue = getOptionValue(option)
              const isSelected = optionValue === value
              return (
                <button
                  key={optionValue}
                  type='button'
                  className={`searchable-select-option${isSelected ? ' is-selected' : ''}`}
                  onClick={() => selectOption(option)}
                >
                  <span>{getOptionLabel(option)}</span>
                  {isSelected && <FiCheck />}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchableSelect
